import React, { useEffect, useMemo, useState } from 'react';
import { Icons } from '../Icons';
import { Dentist, Procedure, ClinicSettings } from '../../types';

interface SettingsProps {
  dentists: Dentist[];
  procedures: Procedure[];
  clinicSettings: ClinicSettings;
  onSave: (data: {
    settings: ClinicSettings;
    dentists: Dentist[];
    procedures: Procedure[];
    rooms: string[];
  }) => void;
}

export const Settings: React.FC<SettingsProps> = ({ dentists, procedures, clinicSettings, onSave }) => {
  const [form, setForm] = useState<ClinicSettings>(clinicSettings);
  const [customDuration, setCustomDuration] = useState<string>('');
  const [localDentists, setLocalDentists] = useState<Dentist[]>(dentists);
  const [localProcedures, setLocalProcedures] = useState<Procedure[]>(procedures);
  const [localRooms, setLocalRooms] = useState<string[]>(clinicSettings.treatmentRooms || []);
  const [newRoom, setNewRoom] = useState('');
  const persistSettings = (overrideForm?: ClinicSettings) => {
    const settingsToSave = overrideForm || form;
    onSave({
      settings: { ...settingsToSave, treatmentRooms: localRooms },
      dentists: localDentists,
      procedures: localProcedures,
      rooms: localRooms,
    });
  };

  useEffect(() => {
    setForm(clinicSettings);
    setLocalRooms(clinicSettings.treatmentRooms || []);
  }, [clinicSettings]);

  useEffect(() => {
    setLocalDentists(dentists);
  }, [dentists]);

  useEffect(() => {
    setLocalProcedures(procedures);
  }, [procedures]);

  const durationOptions = useMemo(() => {
    const deduped = Array.from(new Set([...form.availableSlotDurations])).sort((a, b) => a - b);
    return deduped;
  }, [form.availableSlotDurations]);

  const handleDentistChange = (id: string, key: keyof Dentist, value: string) => {
    setLocalDentists(prev => prev.map(d => (d.id === id ? { ...d, [key]: value } : d)));
  };

  const addDentist = () => {
    setLocalDentists(prev => [
      ...prev,
      { id: `d${Date.now()}`, name: 'New Provider', specialty: 'General Dentistry' },
    ]);
  };

  const removeDentist = (id: string) => {
    setLocalDentists(prev => prev.filter(d => d.id !== id));
  };

  const handleProcedureChange = (id: string, key: keyof Procedure, value: string | number) => {
    setLocalProcedures(prev =>
      prev.map(p => (p.id === id ? { ...p, [key]: value } : p))
    );
  };

  const addProcedure = () => {
    setLocalProcedures(prev => [
      ...prev,
      { id: `pr${Date.now()}`, name: 'New Procedure', defaultDuration: 30 },
    ]);
  };

  const removeProcedure = (id: string) => {
    setLocalProcedures(prev => prev.filter(p => p.id !== id));
  };

  const addRoom = () => {
    const trimmed = newRoom.trim();
    if (!trimmed) return;
    if (localRooms.includes(trimmed)) {
      setNewRoom('');
      return;
    }
    setLocalRooms(prev => [...prev, trimmed]);
    setNewRoom('');
  };

  const removeRoom = (room: string) => {
    setLocalRooms(prev => prev.filter(r => r !== room));
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const updated = { ...form, logoUrl: url };
    setForm(updated);
    // Persist immediately so the top logo updates without extra clicks
    persistSettings(updated);
  };

  const handleFieldChange = (field: keyof ClinicSettings, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleOperatingHourChange = (key: 'start' | 'end', value: string) => {
    setForm(prev => ({
      ...prev,
      operatingHours: { ...prev.operatingHours, [key]: value },
    }));
  };

  const handleAddDuration = () => {
    const num = parseInt(customDuration, 10);
    if (!num || num <= 0) return;
    if (form.availableSlotDurations.includes(num)) {
      setCustomDuration('');
      return;
    }
    setForm(prev => ({
      ...prev,
      availableSlotDurations: [...prev.availableSlotDurations, num],
    }));
    setCustomDuration('');
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      <h2 className="text-3xl font-black">Settings</h2>

      {/* Clinic Information */}
      <div className="bg-white rounded-3xl border shadow-sm p-8 space-y-6 animate-scaleIn">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-blue-100 rounded-xl text-blue-600">
            <Icons.Stethoscope />
          </div>
          <h3 className="text-xl font-black">Clinic Information</h3>
          <div className="ml-auto">
            <button
              type="button"
              onClick={() => persistSettings()}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700"
            >
              Save Clinic Info
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Website / Clinic Name</label>
            <input
              type="text"
              className="w-full border-2 rounded-xl p-3 font-bold"
              value={form.clinicName}
              onChange={(e) => handleFieldChange('clinicName', e.target.value)}
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Logo URL</label>
            <input
              type="text"
              className="w-full border-2 rounded-xl p-3 font-bold"
              placeholder="https://example.com/logo.png"
              value={form.logoUrl || ''}
              onChange={(e) => handleFieldChange('logoUrl', e.target.value)}
            />
            <div className="mt-2 flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="text-sm"
              />
              {form.logoUrl && (
                <img
                  src={form.logoUrl}
                  alt="Logo preview"
                  className="w-12 h-12 object-contain rounded-lg border"
                />
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Phone</label>
            <input
              type="tel"
              className="w-full border-2 rounded-xl p-3 font-bold"
              value={form.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
            />
          </div>

          <div className="col-span-2">
            <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Address</label>
            <input
              type="text"
              className="w-full border-2 rounded-xl p-3 font-bold"
              value={form.address}
              onChange={(e) => handleFieldChange('address', e.target.value)}
            />
          </div>

          <div className="col-span-2">
            <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Email</label>
            <input
              type="email"
              className="w-full border-2 rounded-xl p-3 font-bold"
              value={form.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Providers */}
      <div className="bg-white rounded-3xl border shadow-sm p-8 space-y-6 animate-scaleIn">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-emerald-100 rounded-xl text-emerald-600">
            <Icons.Users />
          </div>
          <h3 className="text-xl font-black">Providers</h3>
        </div>

        <div className="space-y-3">
          {localDentists.map(dentist => (
            <div
              key={dentist.id}
              className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center p-4 bg-slate-50 rounded-2xl"
            >
              <input
                type="text"
                className="w-full border-2 rounded-xl p-3 font-bold"
                value={dentist.name}
                onChange={(e) => handleDentistChange(dentist.id, 'name', e.target.value)}
                placeholder="Provider name"
              />
              <input
                type="text"
                className="w-full border-2 rounded-xl p-3 font-medium"
                value={dentist.specialty}
                onChange={(e) => handleDentistChange(dentist.id, 'specialty', e.target.value)}
                placeholder="Specialty"
              />
              <button
                type="button"
                onClick={() => removeDentist(dentist.id)}
                className="p-3 text-red-600 hover:bg-red-50 rounded-xl"
              >
                <Icons.Trash2 />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addDentist}
          className="px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700"
        >
          + Add Provider
        </button>
      </div>

      {/* Procedures */}
      <div className="bg-white rounded-3xl border shadow-sm p-8 space-y-6 animate-scaleIn">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-purple-100 rounded-xl text-purple-600">
            <Icons.Clock />
          </div>
          <h3 className="text-xl font-black">Procedures</h3>
        </div>

        <div className="space-y-3">
          {localProcedures.map(procedure => (
            <div
              key={procedure.id}
              className="grid grid-cols-[1fr_160px_auto] gap-3 items-center p-4 bg-slate-50 rounded-2xl"
            >
              <input
                type="text"
                className="w-full border-2 rounded-xl p-3 font-bold"
                value={procedure.name}
                onChange={(e) => handleProcedureChange(procedure.id, 'name', e.target.value)}
                placeholder="Procedure name"
              />
              <input
                type="number"
                min={5}
                step={5}
                className="w-full border-2 rounded-xl p-3 font-bold"
                value={procedure.defaultDuration}
                onChange={(e) => handleProcedureChange(procedure.id, 'defaultDuration', parseInt(e.target.value, 10) || 0)}
              />
              <button
                type="button"
                onClick={() => removeProcedure(procedure.id)}
                className="p-3 text-red-600 hover:bg-red-50 rounded-xl"
              >
                <Icons.Trash2 />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addProcedure}
          className="px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700"
        >
          + Add Procedure
        </button>
      </div>

      {/* Rooms */}
      <div className="bg-white rounded-3xl border shadow-sm p-8 space-y-6 animate-scaleIn">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-orange-100 rounded-xl text-orange-600">
            <Icons.Settings />
          </div>
          <h3 className="text-xl font-black">Treatment Rooms</h3>
        </div>

        <div className="flex flex-wrap gap-2">
          {localRooms.map(room => (
            <span
              key={room}
              className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl border"
            >
              <span className="font-bold text-sm">{room}</span>
              <button
                type="button"
                onClick={() => removeRoom(room)}
                className="text-red-600 hover:text-red-700"
              >
                <Icons.X />
              </button>
            </span>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            className="flex-1 border-2 rounded-xl p-3 font-bold"
            placeholder="Add a treatment room"
            value={newRoom}
            onChange={(e) => setNewRoom(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addRoom();
              }
            }}
          />
          <button
            type="button"
            onClick={addRoom}
            className="px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700"
          >
            Add
          </button>
        </div>
      </div>

      {/* Appointment Settings */}
      <div className="bg-white rounded-3xl border shadow-sm p-8 space-y-6 animate-scaleIn">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-blue-100 rounded-xl text-blue-600">
            <Icons.Calendar />
          </div>
          <h3 className="text-xl font-black">Appointment Settings</h3>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">
              Operating Hours
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="time"
                className="flex-1 border-2 rounded-xl p-3 font-bold"
                value={form.operatingHours.start}
                onChange={(e) => handleOperatingHourChange('start', e.target.value)}
              />
              <span className="text-slate-400">to</span>
              <input
                type="time"
                className="flex-1 border-2 rounded-xl p-3 font-bold"
                value={form.operatingHours.end}
                onChange={(e) => handleOperatingHourChange('end', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">
              Default Slot Duration
            </label>
            <select
              className="w-full border-2 rounded-xl p-3 font-bold"
              value={form.defaultSlotDuration}
              onChange={(e) => handleFieldChange('defaultSlotDuration', parseInt(e.target.value, 10))}
            >
              {durationOptions.map(d => (
                <option key={d} value={d}>
                  {d} minutes
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2 mt-2">
              <input
                type="number"
                min={5}
                step={5}
                placeholder="Add custom duration"
                className="flex-1 border-2 rounded-xl p-3 text-sm"
                value={customDuration}
                onChange={(e) => setCustomDuration(e.target.value)}
              />
              <button
                type="button"
                onClick={handleAddDuration}
                className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700"
              >
                Add
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">
              Default Payment Amount
            </label>
            <input
              type="number"
              min={0}
              step={5}
              className="w-full border-2 rounded-xl p-3 font-bold"
              value={form.defaultPaymentAmount ?? 0}
              onChange={(e) => handleFieldChange('defaultPaymentAmount', parseInt(e.target.value, 10) || 0)}
            />
            <p className="text-xs text-slate-500 mt-1">Suggested payment shown when collecting fees.</p>
          </div>

          <div className="col-span-2">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={form.allowDoubleBooking}
                onChange={(e) => handleFieldChange('allowDoubleBooking', e.target.checked)}
              />
              <span className="font-bold text-sm">Prevent double-booking</span>
            </label>
          </div>

          <div className="col-span-2">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={form.showConflictWarnings}
                onChange={(e) => handleFieldChange('showConflictWarnings', e.target.checked)}
              />
              <span className="font-bold text-sm">Show conflict warnings</span>
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center gap-4">
        <p className="text-xs text-slate-400">© Code2Craft</p>
        <button
          type="button"
          onClick={() => persistSettings()}
          className="px-5 py-3 bg-blue-600 text-white rounded-xl font-bold shadow hover:bg-blue-700"
        >
          Save Settings
        </button>
      </div>
    </div>
  );
};
